import React, { FC, useRef, useState } from 'react';

import { MdMoreTime } from 'react-icons/md';
import {
  Button,
  Flex,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';

import AddInventoryCountModal, {
  RefType,
} from 'components/modules/AddInventoryCountModal';
import { UserPill } from 'components/core/UserPill';
import { useMutation } from 'react-query';
import { manualRefreshTrigger, exportCsvItems } from 'api/inventory';

import {
  InventoryCountViewProps,
  Tabs as TabNames,
} from './InventoryCount.props';
import { Filters } from 'components/primitives/Filters';
import { AllInventory } from './AllInventory';
import { ScheduledInventory } from './ScheduledInventory';
import { PendingInventory } from './PendingInventory';
import { ReadyForReportInventory } from './ReadyForReportInventory';
import { DoneInventory } from './DoneInventory';
import { InventoryCount } from 'types/inventory';
import { CSVLink } from 'react-csv';
import { washHub } from 'store/slices';
import { ArchivedInventory } from './ArchivedInventory';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';

const InventoryCountView: FC<InventoryCountViewProps> = ({
  endDate,
  startDate,
  site,
  status,
  defaultIndex,
  accountManagers,
  setDefaultIndex,
  onSearchClear,
  onSearchPress,
  onApplyFilters,
  onClearFilters,
}) => {
  const _selected = { backgroundColor: 'primary.900', color: 'support.500' };
  const _focus = { border: 'none', backgroundColor: 'primary.500' };
  const csvRef = useRef<any>(null);

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';

  const { mutate, isSuccess, isLoading } = useMutation(manualRefreshTrigger, {
    onSuccess: () => {
      setTimeout(() => {
        window.location.reload();
      }, 1200);
    },
  });
  const {
    mutate: mutateCsv,
    data: csvData = '',
    isLoading: isLoadingCSV,
  } = useMutation(
    async () => {
      const response = await exportCsvItems({
        site: site ? site : undefined,
        washHub: washHubId,
        endDate,
        startDate,
        status: undefined,
        accountManagers: accountManagers ? accountManagers : undefined,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click?.();
        }, 500);
      },
    },
  );

  const addInventoryCountModalRef = useRef<RefType>(null);

  const itemCount = (count: InventoryCount): number => {
    const { openItems } = count;
    const sum = openItems.reduce((acc, curr) => {
      const { quantity, sealedQuantity } = curr;
      return acc + (quantity ?? 0) + (sealedQuantity ?? 0);
    }, 0);
    return sum;
  };

  const caseCount = (count: InventoryCount): number => {
    const { openCases } = count;
    const sum = openCases.reduce((acc, curr) => {
      const { quantity, sealedQuantity } = curr;
      return acc + (quantity ?? 0) + (sealedQuantity ?? 0);
    }, 0);
    return sum;
  };

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isLoadingCSV}
          ml={2}
          minW="200px"
          onClick={() => {
            mutateCsv();
          }}>
          Export Counts to CSV
        </Button>
        {!isLoadingCSV && Boolean(csvData) && (
          <a
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvData)}`}
            download={`washhub-${selectedWashHub?.name}-counts.csv`}
            style={{ display: 'none' }}
            ref={csvRef}>
            Export CSV
          </a>
        )}
      </>
    );
  };

  const _props = {
    endDate,
    startDate,
    site,
    accountManagers,
    itemCount,
    caseCount,
  };
  const tabIndex = parseInt(window.location.hash.substring(1));

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          Inventory Count
        </Heading>
        <Flex align="flex-end">
          <Stack isInline>
            {renderExpBtn()}
            <Button
              isDisabled={washHubId === ''}
              mr={2}
              minW="200px"
              leftIcon={<MdMoreTime size="20px" />}
              onClick={() => {
                addInventoryCountModalRef.current?.onOpen?.();
              }}>
              Create New Count
            </Button>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>
      <DateRangeInputExt
        startDate={startDate}
        endDate={endDate}
        onSearchClear={onSearchClear}
        onSearchPress={onSearchPress}
      />
      <Flex mt="50px" justify="space-between">
        <Filters
          venue={site}
          isFullButton
          isSitesOnly
          isAccountManagersOnly
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
        />
      </Flex>
      <Tabs
        variant="enclosed"
        size="lg"
        onChange={setDefaultIndex}
        defaultIndex={tabIndex < Object.keys(TabNames).length ? tabIndex : 0}
        index={defaultIndex}>
        <TabList
          color="support.500"
          _selected={_selected}
          _focus={_focus}
          w="100%">
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            All {/*`(${allInventory})`*/}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Scheduled {/*`(${scheduledInventory})`*/}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Pending Counts {/*`(${pendingInvetory})`*/}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Ready For Report {/*`(${readyForReportInventory})`*/}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Done {/*`(${doneInventory})`*/}
          </Tab>
          <Tab
            _selected={_selected}
            px={12}
            py={3}
            fontSize="15px"
            fontWeight="bold">
            Archived
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>{<AllInventory {..._props} />}</TabPanel>
          <TabPanel p={0}>{<ScheduledInventory {..._props} />}</TabPanel>
          <TabPanel p={0}>{<PendingInventory {..._props} />}</TabPanel>
          <TabPanel p={0}>{<ReadyForReportInventory {..._props} />}</TabPanel>
          <TabPanel p={0}>
            <DoneInventory {..._props} />
          </TabPanel>
          <TabPanel p={0}>
            <ArchivedInventory {..._props} />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <AddInventoryCountModal ref={addInventoryCountModalRef} />
    </Flex>
  );
};

export default InventoryCountView;

/**
 *
 * case 1 - First Report
 *  - Begining Inventory Count = beg.begingDate (ReadOnly)
 *  - ending inventory count (Select Input) = options should be the count date from list
 *
 * case 2 - Final Report
 *  - begining inventory count (Select Input) = options should be the  begining date from the list
 *  - ending inventory count  = ending.countDate (ReadOnly)
 *
 * case 3 - Monthly Report
 *  - begining and ending is going to be null
 *  - begining inventory count (Select Input) show list.begingDate
 *  - ending inventory count (Select Input) show list.
 *
 * case 4 - Event
 *  - both the inputs should be date picker
 *
 */
